import { useState } from 'react';
import { exportDownloadDetails } from '../../../shared/export/download';
import type { AnnotationAction, ExportFormat, OverviewReviewer } from '../../../shared/types';
import Eyebrow from '../Eyebrow';
import { showToast } from '../Toaster';
import { adminApi, ApiError } from '../../lib/api';
import { cardClass, secondaryButtonClass } from './AdminUi';

const FORMATS: Array<{ value: ExportFormat; label: string; detail: string }> = [
  { value: 'markdown', label: 'Markdown', detail: 'Editable paper edit' },
  { value: 'html', label: 'HTML', detail: 'Branded print layout' },
  { value: 'csv', label: 'CSV', detail: 'Annotation rows' },
  { value: 'resolve_csv', label: 'Resolve', detail: 'Timeline marker EDL' },
  { value: 'srt', label: 'SRT', detail: 'Decisions as subtitle cues' },
];

const ACTIONS: Array<{ value: AnnotationAction; label: string }> = [
  { value: 'use', label: 'Use' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'remove', label: 'Remove' },
  { value: 'comment', label: 'Comment' },
];

function toggleValue<T extends string>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

export default function ExportPanel({
  projectId,
  projectTitle,
  reviewers,
}: {
  projectId: string;
  projectTitle: string;
  reviewers: Array<Pick<OverviewReviewer, 'id' | 'display_name'>>;
}) {
  const [format, setFormat] = useState<ExportFormat>('markdown');
  const [actions, setActions] = useState<AnnotationAction[]>(ACTIONS.map(({ value }) => value));
  const [selection, setSelection] = useState(() => ({
    known: reviewers.map(({ id }) => id),
    selected: reviewers.map(({ id }) => id),
  }));
  const currentIds = reviewers.map(({ id }) => id);
  // Adjust during render so a download can never use the previous list. Preserve
  // explicit exclusions; only reviewers absent from the previous list default on.
  const changed = currentIds.length !== selection.known.length ||
    currentIds.some((id, index) => id !== selection.known[index]);
  const reviewerIds = changed
    ? currentIds.filter((id) => !selection.known.includes(id) || selection.selected.includes(id))
    : selection.selected;
  if (changed) setSelection({ known: currentIds, selected: reviewerIds });
  const [busy, setBusy] = useState(false);

  async function download() {
    setBusy(true);
    try {
      const blob = await adminApi.exportProject(projectId, {
        format,
        actions: actions.length === ACTIONS.length ? undefined : actions,
        reviewers: reviewers.length === 0 || reviewerIds.length === reviewers.length
          ? undefined
          : reviewerIds,
      });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = exportDownloadDetails(projectTitle, format).filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      showToast('success', 'Export ready.');
    } catch (caught) {
      showToast(
        'error',
        caught instanceof ApiError
          ? caught.message
          : 'We could not download that export. Mind trying again?',
      );
    } finally {
      setBusy(false);
    }
  }

  const noSelection = actions.length === 0 || (reviewers.length > 0 && reviewerIds.length === 0);
  const activeFormat = FORMATS.find(({ value }) => value === format);
  const selectedFormat = activeFormat?.label ?? 'export';

  return (
    <section className={`${cardClass} rise-subtle p-6 md:p-8`} style={{ animationDelay: '500ms' }} aria-labelledby="export-title">
      <Eyebrow accent>Exports</Eyebrow>
      <h2 id="export-title" className="title-lg mt-3">Take the review into the edit</h2>
      <p className="mt-2 max-w-2xl text-sm text-muted">Choose a format and the decisions to include. Timecodes point back to the original timeline.</p>

      <fieldset className="mt-6">
        <legend className="font-mono text-[11px] uppercase tracking-[0.14em] text-faint">Format</legend>
        <div className="tabs mt-3 flex-wrap">
          {FORMATS.map((option) => {
            const selected = option.value === format;
            return (
              <button
                key={option.value}
                type="button"
                className={`tab ${selected ? 'tab-active' : ''}`}
                aria-pressed={selected}
                onClick={() => setFormat(option.value)}
                title={option.detail}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <p className="mt-2 font-mono text-[11px] text-faint">{activeFormat?.detail}</p>
      </fieldset>

      <div className="mt-7 grid gap-7 md:grid-cols-2">
        <fieldset>
          <legend className="font-mono text-[11px] uppercase tracking-[0.14em] text-faint">Actions</legend>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {ACTIONS.map((action) => (
              <label key={action.value} className="flex min-h-11 items-center gap-3 rounded-input bg-page px-3 text-sm text-muted">
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-accent"
                  checked={actions.includes(action.value)}
                  onChange={() => setActions((current) => toggleValue(current, action.value))}
                />
                {action.label}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="font-mono text-[11px] uppercase tracking-[0.14em] text-faint">Reviewers</legend>
          {reviewers.length === 0 ? (
            <p className="mt-3 text-sm text-muted">No reviewers have contributed yet.</p>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {reviewers.map((reviewer) => (
                <label key={reviewer.id} className="flex min-h-11 items-center gap-3 rounded-input bg-page px-3 text-sm text-muted">
                  <input
                    type="checkbox"
                    className="h-5 w-5 accent-accent"
                    checked={reviewerIds.includes(reviewer.id)}
                    onChange={() => setSelection((current) => ({ ...current, selected: toggleValue(current.selected, reviewer.id) }))}
                  />
                  {reviewer.display_name}
                </label>
              ))}
            </div>
          )}
        </fieldset>
      </div>

      {noSelection ? <p className="mt-5 rounded-input bg-warning-tint p-3 text-sm text-warning">Select at least one action and reviewer to export.</p> : null}
      <button type="button" className={`${secondaryButtonClass} mt-6`} disabled={busy || noSelection} onClick={() => void download()}>
        {busy ? 'Preparing download' : `Download ${selectedFormat}`}
      </button>
    </section>
  );
}
